import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Construction } from "@mui/icons-material";
import { useAccount } from "wagmi";
import { stringToHex, toBytes } from "viem";
import { Stack as MuiStack } from "@mui/material";

import { useApiCall } from "@dzambalaorg/react-hooks";
import { useMetamask, useServerSignature, useWriteContractWithSimulation } from "@dzambalaorg/react-hooks-eth";
import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import { formatItem, getEthPrice } from "@framework/exchange";
import { StyledListWrapper } from "@framework/styled";
import { AddressType, type IContract, type ICraft, type ITemplate, TokenType } from "@framework/types";
import CraftABI from "@framework/abis/json/ExchangeCraftFacet/craft.json";
import { ZeroAddress } from "@framework/constants";
import { Card, List, Typography } from "@dzambalaorg/ui-kit";

import { AllowanceInfoPopover } from "../../../dialogs/allowance";
import { sorter } from "../../../../utils/sorter";

export interface ICraftTemplatePanelProps {
  template: ITemplate;
}

export const CraftTemplatePanel = (props: ICraftTemplatePanelProps) => {
  const { template } = props;

  const referrer = ZeroAddress;

  const { address: walletAddress = "", chainId } = useAccount();
  const { writeContractAsync } = useWriteContractWithSimulation();

  const [rows, setRows] = useState<ICraft[]>([]);

  const { fn: getCraftFn, isLoading } = useApiCall(
    api =>
      api.fetchJson({
        url: "/recipes/craft",
        data: {
          templateId: template.id,
        },
      }),
    { success: false, error: false },
  );

  const getCraft = async () => {
    const json = await getCraftFn();
    setRows(json.rows);
  };

  const metaFnWithSign = useServerSignature(
    async (values: ICraft, sign: IServerSignature, systemContract: IContract) => {
      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: CraftABI,
        functionName: "craft",
        args: [
          {
            externalId: values.id,
            expiresAt: sign.expiresAt,
            nonce: toBytes(sign.nonce, { size: 32 }),
            extra: stringToHex("0x", { size: 32 }),
            receiver: values.merchant!.wallet,
            referrer: ZeroAddress,
          },
          values.item?.components.sort(sorter("id")).map(component => ({
            tokenType: Object.values(TokenType).indexOf(component.tokenType),
            token: component.contract!.address,
            tokenId:
              component.contract!.contractType === TokenType.ERC1155
                ? component.template!.tokens![0].tokenId
                : (component.templateId || 0).toString(), // suppression types check with 0
            amount: component.amount,
          })),
          values.price?.components.sort(sorter("id")).map(component => ({
            tokenType: Object.values(TokenType).indexOf(component.tokenType),
            token: component.contract!.address,
            tokenId: component.template!.tokens![0].tokenId,
            amount: component.amount,
          })),
          sign.signature,
          {
            value: getEthPrice(values.price),
          },
        ],
      });
    },
  );

  const { execute: metaFn } = useMetamask((values: ICraft) => {
    return metaFnWithSign(
      {
        url: "/recipies/craft/sign",
        method: "POST",
        data: {
          chainId,
          account: walletAddress,
          referrer,
          craftId: values.id,
        },
      },
      values,
    ) as Promise<void>;
  });

  const handleCraft = (craft: ICraft) => {
    return async () => await metaFn(craft);
  };

  useEffect(() => {
    void getCraft();
  }, []);

  if (isLoading) {
    return null;
  }

  if (!rows.length) {
    return null;
  }

  return (
    <Card sx={{ height: "fit-content", justifyContent: "flex-start" }}>
      <Card.Content>
        <MuiStack direction="row" justifyContent="space-between">
          <Typography variant="h5" component="p">
            <FormattedMessage id="pages.token.craft" />
          </Typography>
          <AllowanceInfoPopover />
        </MuiStack>
        <StyledListWrapper count={rows.length} isLoading={isLoading}>
          {rows.map(craft => {
            return (
              <List.ItemButton key={craft.id} onClick={handleCraft(craft)}>
                <List.ItemIcon>
                  <Construction />
                </List.ItemIcon>
                <List.ItemText>{formatItem(craft.price)}</List.ItemText>
              </List.ItemButton>
            );
          })}
        </StyledListWrapper>
      </Card.Content>
    </Card>
  );
};
